import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useEffect } from 'react';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { api } from '@/shared/api/axios';
import {
  Settings, Landmark, ArrowLeftRight, FileText, Hash,
  Shield, Calendar, CheckCircle2, AlertTriangle, Loader2,
  ChevronRight, Lock, Unlock, RefreshCw, Info
} from 'lucide-react';

// ── Shared Styles ─────────────────────────────────────────────────────────────
const INPUT = 'w-full rounded-lg border border-input bg-background px-3 py-2 text-body-md text-on-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const SELECT = INPUT + ' cursor-pointer';
const LABEL = 'mb-1.5 block text-label-sm font-semibold uppercase tracking-wider text-on-surface-variant';
const SECTION = 'space-y-4 rounded-card border border-border bg-card p-5 shadow-xs';
const SECTION_TITLE = 'border-b border-border pb-3 text-headline-sm font-semibold text-on-surface';
const BTN_PRIMARY = 'flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-body-sm font-semibold text-on-primary transition-colors hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';
const BTN_OUTLINE = 'flex items-center gap-2 rounded-lg border border-outline bg-card px-4 py-2 text-body-sm font-medium text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * A control that has no server endpoint yet is shown disabled with its reason,
 * never as a live button that silently does nothing.
 */
function UnwiredNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-card-sm border border-warning/25 bg-warning/10 px-3 py-2 text-body-sm text-warning-text">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

// ── Tab IDs ───────────────────────────────────────────────────────────────────
type TabId = 'general' | 'control-accounts' | 'mappings' | 'posting' | 'numbering' | 'approvals' | 'periods' | 'readiness';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'control-accounts', label: 'Control Accounts', icon: Landmark },
  { id: 'mappings', label: 'Mappings', icon: ArrowLeftRight },
  { id: 'posting', label: 'Posting', icon: FileText },
  { id: 'numbering', label: 'Numbering', icon: Hash },
  { id: 'approvals', label: 'Approvals', icon: Shield },
  { id: 'periods', label: 'Periods', icon: Calendar },
  { id: 'readiness', label: 'Readiness', icon: CheckCircle2 },
];

// ── Helper: Info Tooltip ──────────────────────────────────────────────────────
function InfoNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-card-sm border border-info/20 bg-info/10 px-3 py-2 text-body-sm text-info-text">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function WarningNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-card-sm border border-warning/25 bg-warning/10 px-3 py-2 text-body-sm text-warning-text">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

// ── Tab: General ─────────────────────────────────────────────────────────────
function GeneralTab() {
  const [form, setForm] = useState({
    baseCurrency: 'INR',
    fiscalYearStart: '04', // April
    timezone: 'Asia/Kolkata',
    decimalPrecision: '2',
    roundingTolerance: '0.50',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/accounting/org/settings', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      // settings endpoint may not be wired yet
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Currency &amp; Locale</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Base Currency</label>
            <select className={SELECT} value={form.baseCurrency} onChange={e => setForm(f => ({ ...f, baseCurrency: e.target.value }))}>
              {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Business Timezone</label>
            <select className={SELECT} value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
              {['Asia/Kolkata', 'UTC', 'America/New_York', 'Europe/London', 'Asia/Dubai'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Decimal Precision</label>
            <select className={SELECT} value={form.decimalPrecision} onChange={e => setForm(f => ({ ...f, decimalPrecision: e.target.value }))}>
              {['0', '1', '2', '3', '4'].map(p => <option key={p}>{p} decimal places</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Rounding Tolerance (minor units)</label>
            <input className={INPUT} type="number" min="0" step="0.01" value={form.roundingTolerance}
              onChange={e => setForm(f => ({ ...f, roundingTolerance: e.target.value }))} />
          </div>
        </div>
        <InfoNote text="Money is stored in paise (minor units as integers). The base currency cannot be changed once live journals exist." />
      </div>

      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Fiscal Year</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Fiscal Year Start Month</label>
            <select className={SELECT} value={form.fiscalYearStart} onChange={e => setForm(f => ({ ...f, fiscalYearStart: e.target.value }))}>
              {[
                ['01', 'January'], ['02', 'February'], ['03', 'March'], ['04', 'April'],
                ['05', 'May'], ['06', 'June'], ['07', 'July'], ['08', 'August'],
                ['09', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
              ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <InfoNote text="Fiscal year start determines how periods, P&L and Balance Sheet are computed. Changes apply to new fiscal years only." />
      </div>

      <div className="flex justify-end">
        <button className={BTN_PRIMARY} onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saved ? '✓ Saved' : 'Save General Settings'}
        </button>
      </div>
    </div>
  );
}

// ── Tab: Control Accounts ─────────────────────────────────────────────────────
function ControlAccountsTab() {
  const [accounts, setAccounts] = useState<Record<string, string>>({
    ar: '1100', ap: '2100', inventory: '1300',
    retainedEarnings: '3200', openingBalanceEquity: '3100',
    rounding: '4900', suspense: '9999',
    undepositedFunds: '1050', cashOverShort: '9001', forexGainLoss: '7800',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const controlItems = [
    { key: 'ar', label: 'Accounts Receivable (AR)', desc: 'Customer subledger control account — no manual postings' },
    { key: 'ap', label: 'Accounts Payable (AP)', desc: 'Supplier subledger control account — no manual postings' },
    { key: 'inventory', label: 'Inventory Asset', desc: 'Cleared by COGS on sale; increased by goods receipt' },
    { key: 'retainedEarnings', label: 'Retained Earnings', desc: 'Year-end closing journal destination' },
    { key: 'openingBalanceEquity', label: 'Opening Balance Equity', desc: 'Temporary account for opening balance migration' },
    { key: 'rounding', label: 'Rounding Difference', desc: 'Absorbs sub-paisa differences in tax calculations' },
    { key: 'suspense', label: 'Suspense Account', desc: 'Must be zero at period close; blocks close if non-zero' },
    { key: 'undepositedFunds', label: 'Undeposited Funds', desc: 'Cash/card receipts pending bank deposit' },
    { key: 'cashOverShort', label: 'Cash Over/Short', desc: 'POS register variance absorption' },
    { key: 'forexGainLoss', label: 'Forex Gain/Loss', desc: 'Unrealised and realised foreign exchange differences' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/accounting/org/control-accounts', accounts);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { /* endpoint may be pending */ }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <WarningNote text="Changing a control account while the current account has a non-zero balance is blocked. Transfer the balance by manual journal first." />
      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Control Account Codes</h3>
        <div className="space-y-3">
          {controlItems.map(item => (
            <div key={item.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center py-2 border-b border-slate-50 dark:border-slate-800 last:border-0">
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{item.label}</p>
                <p className="text-xs text-slate-400">{item.desc}</p>
              </div>
              <input
                className={INPUT + ' md:col-span-2 font-mono'}
                value={accounts[item.key] || ''}
                onChange={e => setAccounts(a => ({ ...a, [item.key]: e.target.value }))}
                placeholder="Account code e.g. 1100"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end">
        <button className={BTN_PRIMARY} onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saved ? '✓ Saved' : 'Save Control Accounts'}
        </button>
      </div>
    </div>
  );
}

// ── Tab: Posting Rules ────────────────────────────────────────────────────────
function PostingTab() {
  const [settings, setSettings] = useState({
    salePostingEvent: 'invoice_finalized',
    purchasePostingEvent: 'bill_posted',
    expensePostingEvent: 'expense_approved',
    discountPolicy: 'reduce_revenue',
    revenueRecognitionPoint: 'invoice',
    landedCostDefault: 'value',
    negativeStockPolicy: 'block',
  });

  return (
    <div className="space-y-5">
      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Posting Triggers</h3>
        <InfoNote text="Posting-event changes are prospective only. Historical journals are unaffected. A confirmation is required." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <label className={LABEL}>Sale Posts When</label>
            <select className={SELECT} value={settings.salePostingEvent}
              onChange={e => setSettings(s => ({ ...s, salePostingEvent: e.target.value }))}>
              <option value="invoice_finalized">Invoice Finalised</option>
              <option value="payment_received">Payment Received</option>
              <option value="shipment_completed">Shipment Completed</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Purchase Posts When</label>
            <select className={SELECT} value={settings.purchasePostingEvent}
              onChange={e => setSettings(s => ({ ...s, purchasePostingEvent: e.target.value }))}>
              <option value="bill_posted">Supplier Bill Posted</option>
              <option value="goods_received">Goods Received (GRN)</option>
              <option value="payment_made">Payment Made</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Expense Posts When</label>
            <select className={SELECT} value={settings.expensePostingEvent}
              onChange={e => setSettings(s => ({ ...s, expensePostingEvent: e.target.value }))}>
              <option value="expense_approved">Expense Approved</option>
              <option value="expense_paid">Expense Paid</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Revenue Recognition Point</label>
            <select className={SELECT} value={settings.revenueRecognitionPoint}
              onChange={e => setSettings(s => ({ ...s, revenueRecognitionPoint: e.target.value }))}>
              <option value="invoice">At Invoice (Accrual)</option>
              <option value="shipment">At Shipment</option>
              <option value="payment">At Payment (Cash)</option>
            </select>
          </div>
        </div>
      </div>

      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Discount &amp; Landed Cost Policy</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Discount Policy</label>
            <select className={SELECT} value={settings.discountPolicy}
              onChange={e => setSettings(s => ({ ...s, discountPolicy: e.target.value }))}>
              <option value="reduce_revenue">Reduce Revenue (Net Method)</option>
              <option value="separate_discount_account">Post to Discount Account</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Landed Cost Default Allocation</label>
            <select className={SELECT} value={settings.landedCostDefault}
              onChange={e => setSettings(s => ({ ...s, landedCostDefault: e.target.value }))}>
              <option value="value">By Value</option>
              <option value="quantity">By Quantity</option>
              <option value="weight">By Weight</option>
              <option value="volume">By Volume</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Negative Stock Policy</label>
            <select className={SELECT} value={settings.negativeStockPolicy}
              onChange={e => setSettings(s => ({ ...s, negativeStockPolicy: e.target.value }))}>
              <option value="block">Block Sale (Recommended)</option>
              <option value="allow_with_warning">Allow with Warning</option>
              <option value="allow_silent">Allow Silently</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end gap-2">
        <UnwiredNote>Posting rules are shown for reference. Saving them is not connected to the server yet.</UnwiredNote>
        <button type="button" className={BTN_PRIMARY} disabled aria-disabled="true">Save Posting Rules</button>
      </div>
    </div>
  );
}

// ── Tab: Numbering ────────────────────────────────────────────────────────────
function NumberingTab() {
  const series = [
    { key: 'journal', label: 'Journal Entries', prefix: 'JV-', padding: 5 },
    { key: 'receipt', label: 'Receipts', prefix: 'RCP-', padding: 5 },
    { key: 'payment', label: 'Payments', prefix: 'PMT-', padding: 5 },
    { key: 'debit_note', label: 'Debit Notes', prefix: 'DN-', padding: 5 },
    { key: 'credit_note', label: 'Credit Notes', prefix: 'CN-', padding: 5 },
    { key: 'grn', label: 'Goods Receipts', prefix: 'GRN-', padding: 5 },
    { key: 'year_end', label: 'Year-End Journals', prefix: 'YE-', padding: 4 },
  ];

  const [forms, setForms] = useState<Record<string, { prefix: string; padding: number }>>(
    Object.fromEntries(series.map(s => [s.key, { prefix: s.prefix, padding: s.padding }]))
  );

  return (
    <div className="space-y-5">
      <InfoNote text="Series prefixes must be unique across all document types. Changes apply to new documents only; existing numbers are never renumbered." />
      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Document Number Series</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                {['Document Type', 'Prefix', 'Zero Padding', 'Example'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {series.map(s => {
                const form = forms[s.key];
                const example = `${form.prefix}${'0'.repeat(Math.max(0, form.padding - 1))}1`;
                return (
                  <tr key={s.key} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="px-4 py-3 font-medium">{s.label}</td>
                    <td className="px-4 py-3 w-36">
                      <input className={INPUT + ' font-mono'} value={form.prefix}
                        onChange={e => setForms(f => ({ ...f, [s.key]: { ...form, prefix: e.target.value } }))} />
                    </td>
                    <td className="px-4 py-3 w-28">
                      <input type="number" min="3" max="10" className={INPUT + ' text-center'} value={form.padding}
                        onChange={e => setForms(f => ({ ...f, [s.key]: { ...form, padding: Number(e.target.value) } }))} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-indigo-600 dark:text-indigo-400">{example}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <UnwiredNote>These prefixes are a local default list — they are not read from the server, and edits here are not saved.</UnwiredNote>
        <button type="button" className={BTN_PRIMARY} disabled aria-disabled="true">Save Numbering</button>
      </div>
    </div>
  );
}

// ── Tab: Approvals ────────────────────────────────────────────────────────────
function ApprovalsTab() {
  const [settings, setSettings] = useState({
    requireJournalApproval: false,
    requirePaymentApproval: false,
    segregationOfDuties: false,
    highValueThresholdMinor: 100000 * 100, // ₹1,00,000
    makerCheckerEnabled: false,
  });

  const Toggle = ({ label, desc, field }: { label: string; desc: string; field: keyof typeof settings }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</p>
        <p className="text-xs text-slate-400">{desc}</p>
      </div>
      <button
        onClick={() => setSettings(s => ({ ...s, [field]: !s[field] }))}
        className={`relative w-12 h-6 rounded-full transition-colors ${settings[field as keyof typeof settings] ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'}`}
      >
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${settings[field as keyof typeof settings] ? 'left-7' : 'left-1'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Approval Workflow</h3>
        <Toggle label="Require Journal Approval" desc="Manual journals go through a submit → approve workflow before posting" field="requireJournalApproval" />
        <Toggle label="Require Payment Approval" desc="Payments above the threshold require secondary approval" field="requirePaymentApproval" />
        <Toggle label="Maker–Checker (Segregation of Duties)" desc="The creator cannot be the approver for the same document" field="makerCheckerEnabled" />
        <Toggle label="Segregation of Duties (Posting)" desc="The posting user must differ from the data-entry user" field="segregationOfDuties" />
      </div>

      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Approval Limits</h3>
        <div>
          <label className={LABEL}>High-Value Threshold (₹) — requires approval above this amount</label>
          <input type="number" className={INPUT + ' max-w-xs'} min="0"
            value={settings.highValueThresholdMinor / 100}
            onChange={e => setSettings(s => ({ ...s, highValueThresholdMinor: Number(e.target.value) * 100 }))} />
          <p className="text-xs text-slate-400 mt-1">Current: ₹{(settings.highValueThresholdMinor / 100).toLocaleString('en-IN')}</p>
        </div>
      </div>

      <div className="flex flex-col items-end gap-2">
        <UnwiredNote>These toggles and the threshold start from this screen's own defaults — they are not loaded from the server, and saving is not connected.</UnwiredNote>
        <button type="button" className={BTN_PRIMARY} disabled aria-disabled="true">Save Approval Settings</button>
      </div>
    </div>
  );
}

// ── Tab: Periods ──────────────────────────────────────────────────────────────
interface FiscalYear { _id: string; name: string; startDate: string; endDate: string; status: string; }
interface Period { _id: string; name: string; startDate: string; endDate: string; status: string; fiscalYearId: string; }

function PeriodsTab() {
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedFY, setSelectedFY] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [showReopen, setShowReopen] = useState<string | null>(null);

  // New FY form
  const [newFY, setNewFY] = useState({ name: '', startYear: new Date().getFullYear(), startMonth: 4, retainedEarningsAccountId: '3200' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/api/accounting/fiscal-years')
      .then(r => {
        const items: FiscalYear[] = r.data.data || [];
        setFiscalYears(items);
        if (items.length > 0) setSelectedFY(items[0]._id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedFY) return;
    api.get(`/api/accounting/periods?fiscalYearId=${selectedFY}`)
      .then(r => setPeriods(r.data.data || []))
      .catch(() => {});
  }, [selectedFY]);

  const closePeriod = async (id: string) => {
    if (!confirm('Close this period? This will block new postings.')) return;
    setActionLoading(id);
    try {
      await api.post(`/api/accounting/periods/${id}/close`, {});
      setPeriods(p => p.map(x => x._id === id ? { ...x, status: 'closed' } : x));
    } catch (e: any) { alert(e.response?.data?.error?.message || e.message); }
    finally { setActionLoading(null); }
  };

  const reopenPeriod = async (id: string) => {
    if (reopenReason.length < 20) { alert('Reason must be at least 20 characters.'); return; }
    setActionLoading(id);
    try {
      await api.post(`/api/accounting/periods/${id}/reopen`, { reason: reopenReason });
      setPeriods(p => p.map(x => x._id === id ? { ...x, status: 'open' } : x));
      setShowReopen(null); setReopenReason('');
    } catch (e: any) { alert(e.response?.data?.error?.message || e.message); }
    finally { setActionLoading(null); }
  };

  const createFiscalYear = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post('/api/accounting/fiscal-years', newFY);
      const fy: FiscalYear = r.data.data;
      setFiscalYears(prev => [fy, ...prev]);
      setSelectedFY(fy._id);
    } catch (e: any) { alert(e.response?.data?.error?.message || e.message); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-5">
      {/* Create FY */}
      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Create Fiscal Year &amp; Periods</h3>
        <form onSubmit={createFiscalYear} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className={LABEL}>Name</label>
            <input required className={INPUT} placeholder="FY 2025-26" value={newFY.name}
              onChange={e => setNewFY(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className={LABEL}>Start Year</label>
            <input required type="number" className={INPUT} value={newFY.startYear}
              onChange={e => setNewFY(f => ({ ...f, startYear: Number(e.target.value) }))} />
          </div>
          <div>
            <label className={LABEL}>Start Month</label>
            <select className={SELECT} value={newFY.startMonth}
              onChange={e => setNewFY(f => ({ ...f, startMonth: Number(e.target.value) }))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('en-IN', { month: 'long' })}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={creating} className={BTN_PRIMARY}>
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            Generate 12 Periods
          </button>
        </form>
      </div>

      {/* FY Selector + Periods */}
      <div className={SECTION}>
        <div className="flex items-center justify-between">
          <h3 className={SECTION_TITLE.replace('pb-3 border-b border-slate-100 dark:border-slate-800', '')}>Accounting Periods</h3>
          <select className={SELECT + ' w-48'} value={selectedFY} onChange={e => setSelectedFY(e.target.value)}>
            {fiscalYears.map(fy => <option key={fy._id} value={fy._id}>{fy.name}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
        ) : periods.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No periods found. Generate a fiscal year above.</p>
        ) : (
          <div className="overflow-x-auto mt-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  {['Period', 'Start', 'End', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {periods.map(p => (
                  <tr key={p._id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-slate-500">{new Date(p.startDate).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3 text-slate-500">{new Date(p.endDate).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        p.status === 'closed' ? 'bg-red-100 text-red-700' :
                        p.status === 'open' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {p.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.status === 'open' ? (
                        <button
                          onClick={() => closePeriod(p._id)}
                          disabled={actionLoading === p._id}
                          className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 font-medium border border-rose-200 rounded-lg px-2.5 py-1 hover:bg-rose-50 transition-colors disabled:opacity-50"
                        >
                          {actionLoading === p._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                          Close Period
                        </button>
                      ) : p.status === 'closed' ? (
                        showReopen === p._id ? (
                          <div className="flex items-center gap-2">
                            <input
                              className={INPUT + ' text-xs py-1 w-60'}
                              placeholder="Reopen reason (min 20 chars)…"
                              value={reopenReason}
                              onChange={e => setReopenReason(e.target.value)}
                            />
                            <button
                              onClick={() => reopenPeriod(p._id)}
                              disabled={actionLoading === p._id || reopenReason.length < 20}
                              className="flex items-center gap-1 text-xs text-amber-600 font-medium border border-amber-200 rounded-lg px-2.5 py-1 hover:bg-amber-50 disabled:opacity-50"
                            >
                              {actionLoading === p._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                              Confirm
                            </button>
                            <button onClick={() => setShowReopen(null)} className="text-xs text-slate-400">Cancel</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowReopen(p._id)}
                            className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium border border-amber-200 rounded-lg px-2.5 py-1 hover:bg-amber-50 transition-colors"
                          >
                            <Unlock className="w-3 h-3" /> Reopen
                          </button>
                        )
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Readiness Validator ──────────────────────────────────────────────────
interface ReadinessItem { key: string; label: string; status: 'ok' | 'warning' | 'error'; detail?: string; }

function ReadinessTab() {
  const [items, setItems] = useState<ReadinessItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<string | null>(null);

  const runCheck = async () => {
    setLoading(true);
    setFailure(null);
    try {
      const r = await api.get('/api/accounting/readiness');
      const payload = r.data.data || r.data || {};
      const checks: ReadinessItem[] = Object.entries(payload).map(([key, val]: [string, any]) => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        status: val === true || val === 'ok' ? 'ok' : (val === 'warning' ? 'warning' : 'error'),
        detail: typeof val === 'string' ? val : undefined,
      }));
      setItems(checks);
      setLastChecked(new Date().toLocaleTimeString('en-IN'));
    } catch (e: any) {
      // This used to render eight INVENTED verdicts ("Default RBM chart loaded
      // (158 accounts)", "Balance is zero", "All 24 accounting permissions
      // wired") and stamp a check time as if it had succeeded. This panel is the
      // go-live gate: a fabricated green is the most expensive lie in the app.
      // On failure there is now no data and no timestamp — only the reason and
      // a retry.
      setItems([]);
      setLastChecked(null);
      // /api/accounting/* answers { ok:false, error:{ code, message, details } },
      // so reading .error as a string rendered "[object Object]" as the reason a
      // statutory gate failed. Take the message, fall back to the flat shape other
      // routers use, and never show a bare object.
      setFailure(String(
        e?.response?.data?.error?.message
        || (typeof e?.response?.data?.error === 'string' ? e.response.data.error : '')
        || e?.message
        || 'The readiness check could not run.',
      ));
    }
    finally { setLoading(false); }
  };

  useEffect(() => { runCheck(); }, []);

  const statusIcon = (s: ReadinessItem['status']) => {
    if (s === 'ok') return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (s === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    return <AlertTriangle className="w-4 h-4 text-rose-600" />;
  };

  const blockers = items.filter(i => i.status === 'error').length;
  const warnings = items.filter(i => i.status === 'warning').length;
  const ok = items.filter(i => i.status === 'ok').length;

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-black text-emerald-600">{ok}</p>
          <p className="text-xs text-emerald-700 font-semibold mt-0.5">Passing</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-black text-amber-600">{warnings}</p>
          <p className="text-xs text-amber-700 font-semibold mt-0.5">Warnings</p>
        </div>
        <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-black text-rose-600">{blockers}</p>
          <p className="text-xs text-rose-700 font-semibold mt-0.5">Blockers</p>
        </div>
      </div>

      {blockers > 0 && (
        <WarningNote text={`${blockers} blocker(s) must be resolved before the accounting module can be enabled. The feature flag will be rejected server-side while blockers exist.`} />
      )}

      <div className={SECTION}>
        <div className="flex items-center justify-between">
          <h3 className={SECTION_TITLE.replace('pb-3 border-b border-slate-100 dark:border-slate-800', '')}>System Readiness Checks</h3>
          <div className="flex items-center gap-3">
            {lastChecked && <span className="text-xs text-slate-400">Last checked: {lastChecked}</span>}
            <button onClick={runCheck} disabled={loading} className={BTN_OUTLINE + ' text-xs py-1.5'}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Re-check
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
        ) : failure ? (
          <div className="mt-3 rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/20 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Readiness could not be evaluated</p>
                <p className="text-xs mt-1 text-slate-600 dark:text-slate-300">
                  {failure} Nothing below is assumed to be fine — an unreadiness check that cannot run
                  reports nothing, rather than guessing.
                </p>
                <button onClick={runCheck} className={BTN_OUTLINE + ' text-xs py-1.5 mt-3'}>Try again</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 mt-3">
            {items.map(item => (
              <div key={item.key} className={`flex items-center gap-3 p-3.5 rounded-lg border transition-colors ${
                item.status === 'ok' ? 'border-emerald-100 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-950/10' :
                item.status === 'warning' ? 'border-amber-100 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/10' :
                'border-rose-100 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/10'
              }`}>
                {statusIcon(item.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.label}</p>
                  {item.detail && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.detail}</p>}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Mappings Stub ────────────────────────────────────────────────────────
function MappingsTab() {
  return (
    <div className="space-y-5">
      <InfoNote text="Mappings link operational documents (products, tax rates, payment methods, expense categories) to accounting ledger accounts. Unmapped items appear in the Exceptions queue and block posting." />
      {[
        { label: 'Products & Categories', count: 3, link: 'Configure product GL account mappings — Revenue, COGS, Inventory, Return, Adjustment' },
        { label: 'Tax Rates', count: 1, link: 'Map each tax rate to the correct Input/Output Tax control account' },
        { label: 'Payment Methods', count: 0, link: 'Map cash, card, UPI, bank transfer to the correct bank/cash ledger' },
        { label: 'Expense Categories', count: 0, link: 'Map expense categories to their expense ledger accounts' },
        { label: 'Business Locations', count: 0, link: 'Per-location Revenue, Inventory, Tax and Cash account overrides' },
      ].map(item => (
        <div key={item.label} className={SECTION + ' flex items-center justify-between'}>
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.label}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{item.link}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {item.count > 0 && (
              <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">{item.count} unmapped</span>
            )}
            {item.count === 0 && (
              <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">All mapped</span>
            )}
            <button className={BTN_OUTLINE + ' text-xs py-1.5'}>Configure <ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function AccountingSettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('general');

  return (
    <div className="">
      <AccountingSubNav />
      <div className="mx-auto max-w-[1580px] pb-16">

        {/* Page Header */}
        <div className="mb-6">
          <PageHeader icon={Settings} eyebrow="Finance" title="Accounting Settings" description={<>Configure your accounting engine, periods, and posting rules</>} />
        </div>

        <div className="flex gap-6">
          {/* Left sidebar nav */}
          <div className="w-48 shrink-0">
            <nav className="space-y-0.5">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                      activeTab === tab.id
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            {activeTab === 'general' && <GeneralTab />}
            {activeTab === 'control-accounts' && <ControlAccountsTab />}
            {activeTab === 'mappings' && <MappingsTab />}
            {activeTab === 'posting' && <PostingTab />}
            {activeTab === 'numbering' && <NumberingTab />}
            {activeTab === 'approvals' && <ApprovalsTab />}
            {activeTab === 'periods' && <PeriodsTab />}
            {activeTab === 'readiness' && <ReadinessTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountingSettingsPage;
